// src/pages/pedidos/EliminarPedido.js
import React from 'react'
import Modal from 'react-bootstrap/Modal'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Badge from 'react-bootstrap/Badge'

import { getTipoColor, OPCIONES_TIPOS_PEDIDO_PARA_FILTRO, calcularEstados } from 'config/pedidos'
import { formatNumberForDisplay } from 'utils/numberUtils'

function EliminarPedido(props) {
	const { pedido, cancelar, mostrar, eliminar } = props

	const handleClose = () => {
		cancelar()
	}

	const handleEliminar = () => {
		eliminar(pedido?.id)
	}

	const formatearFecha = (fecha) => {
		if (!fecha) return '-'
		return new Date(fecha).toLocaleDateString('es-PY', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		})
	}

	const formatearPrecio = (precio) => {
		if (!precio || precio === 0) return "₲ 0"
		return `₲ ${formatNumberForDisplay(precio.toString())}`
	}

	// Calcular estados del pedido
	const estados = pedido ? calcularEstados(pedido) : {}

	if (!pedido) return null

	return (
		<Modal show={mostrar} onHide={handleClose} backdrop="static" centered>
			<Modal.Header closeButton>
				<Modal.Title>
					<i className="fa fa-trash text-danger me-2"></i>
					Eliminar Pedido
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Alert variant="danger">
					<i className="fa fa-exclamation-triangle me-2"></i>
					<strong>¡Atención!</strong> Está a punto de eliminar permanentemente el pedido #{pedido.id}.
					Esta acción no se puede deshacer.
				</Alert>

				{/* Información del pedido a eliminar */}
				<div className="mb-4">
					<h6 className="text-muted mb-3">Información del pedido:</h6>

					<div className="border rounded p-3 bg-light">
						<div className="row mb-2">
							<div className="col-4">
								<strong>ID:</strong>
							</div>
							<div className="col-8">
								#{pedido.id}
							</div>
						</div>

						<div className="row mb-2">
							<div className="col-4">
								<strong>Cliente:</strong>
							</div>
							<div className="col-8">
								{pedido.usuario ?
									`${pedido.usuario.nombre} ${pedido.usuario.apellido}` :
									(pedido.datosFacturacion?.razonSocial || 'Cliente no especificado')
								}
							</div>
						</div>

						<div className="row mb-2">
							<div className="col-4">
								<strong>Tipo:</strong>
							</div>
							<div className="col-8">
								<Badge bg={getTipoColor(pedido.tipo)} className="badge-tipo">
									{OPCIONES_TIPOS_PEDIDO_PARA_FILTRO.find(t => t.value === pedido.tipo)?.label || pedido.tipo}
								</Badge>
							</div>
						</div>

						<div className="row mb-2">
							<div className="col-4">
								<strong>Total:</strong>
							</div>
							<div className="col-8">
								<span className="text-primary fw-bold">
									{formatearPrecio(pedido.montoTotal)}
								</span>
							</div>
						</div>

						<div className="row mb-2">
							<div className="col-4">
								<strong>Fecha:</strong>
							</div>
							<div className="col-8">
								{formatearFecha(pedido.recibido)}
							</div>
						</div>

						{/* Estados actuales */}
						{estados && (
							<div className="row">
								<div className="col-4">
									<strong>Estados:</strong>
								</div>
								<div className="col-8">
									<div className="d-flex flex-wrap gap-1">
										{estados.procesando && (
											<Badge bg="warning" className="me-1">Procesando</Badge>
										)}
										{estados.listo && (
											<Badge bg="info" className="me-1">Listo</Badge>
										)}
										{estados.enCamino && (
											<Badge bg="primary" className="me-1">En camino</Badge>
										)}
										{estados.entregado && (
											<Badge bg="success" className="me-1">Entregado</Badge>
										)}
										{estados.pagado && (
											<Badge bg="success" className="me-1">Pagado</Badge>
										)}
										{estados.cancelado && (
											<Badge bg="danger" className="me-1">Cancelado</Badge>
										)}
										{estados.finalizado && (
											<Badge bg="secondary" className="me-1">Finalizado</Badge>
										)}
									</div>
								</div>
							</div>
						)}
					</div>
				</div>

				{/* Advertencias específicas según el estado */}
				{estados?.entregado && !estados?.cancelado && (
					<Alert variant="warning">
						<i className="fa fa-exclamation-circle me-2"></i>
						<strong>Advertencia:</strong> Este pedido ya fue entregado. Eliminar un pedido entregado
						puede afectar los registros de ventas y reportes.
					</Alert>
				)}

				{estados?.pagado && !estados?.cancelado && (
					<Alert variant="warning">
						<i className="fa fa-credit-card me-2"></i>
						<strong>Advertencia:</strong> Este pedido ya fue pagado. Considere cancelar el pedido
						en lugar de eliminarlo para mantener el registro financiero.
					</Alert>
				)}

				{!estados?.cancelado && (estados?.procesando || estados?.listo || estados?.enCamino) && (
					<Alert variant="info">
						<i className="fa fa-info-circle me-2"></i>
						<strong>Sugerencia:</strong> Este pedido está en proceso. Considere cancelarlo primero
						antes de eliminarlo para notificar al cliente.
					</Alert>
				)}

				<p className="mb-0 text-center">
					<strong>¿Está seguro que desea eliminar este pedido?</strong>
				</p>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="outline-secondary" onClick={handleClose}>
					<i className="fa fa-times me-2"></i>
					Cancelar
				</Button>
				<Button variant="danger" onClick={handleEliminar}>
					<i className="fa fa-trash me-2"></i>
					Eliminar Pedido
				</Button>
			</Modal.Footer>
		</Modal>
	)
}

export default EliminarPedido